// ================================================================
// ConsultationsPage — clinician evaluations over clinical.evaluation_template
// Preoperative Behavioral Health Consultation (10 domains + specialty
// modules) and Fitness/Suitability MH Evaluation. Four governed signed
// conclusions only — no pass/fail exists in this UI or the data model.
// Signed evaluations are DB-trigger immutable.
// ================================================================
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const card = { background: '#0d1526', border: '1px solid #1f2b45', borderRadius: 10, padding: 20, marginBottom: 16 }
const h = { color: '#c9a96e', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 12px' }
const inp = { width: '100%', background: '#060e1c', border: '1px solid #374151', borderRadius: 6, padding: '8px 10px', color: '#fff', fontSize: 14, fontFamily: 'inherit' }
const btn = { background: '#c9a96e', color: '#060e1c', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' }
const ghost = { background: 'transparent', color: '#c9a96e', border: '1px solid #c9a96e', borderRadius: 8, padding: '8px 18px', cursor: 'pointer' }

export default function ConsultationsPage() {
  const [templates, setTemplates] = useState([])
  const [evals, setEvals] = useState([])
  const [draft, setDraft] = useState(null)   // {template, patient_id, module, findings, outcome, rationale, evaluation_id?}
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = async () => {
    const { data: t } = await supabase.schema('clinical').from('evaluation_template').select('*').eq('active', true)
    setTemplates(t ?? [])
    const { data: e } = await supabase.schema('clinical').from('evaluation')
      .select('*').order('insert_date', { ascending: false }).limit(25)
    setEvals(e ?? [])
  }
  useEffect(() => { load() }, [])

  const start = (tpl) => setDraft({ template: tpl, patient_id: '', module: '', findings: {}, outcome: '', rationale: '' })

  const save = async (sign) => {
    setBusy(true); setErr('')
    const t = draft.template
    if (!draft.patient_id) { setErr('Patient ID is required.'); setBusy(false); return }
    if (sign) {
      const missing = (t.domains ?? []).filter(d => d.required && !(draft.findings[d.key] ?? '').trim())
      if (missing.length) { setErr(`Required domains incomplete: ${missing.map(d => d.name).join('; ')}`); setBusy(false); return }
      if (!draft.outcome || !(draft.rationale ?? '').trim()) { setErr('A signed conclusion and narrative rationale are required.'); setBusy(false); return }
    }
    await supabase.auth.getUser()
    const payload = {
      patient_id: Number(draft.patient_id),
      template_key: t.template_key,
      specialty_module: draft.module || null,
      requested_by_app: 'mind',
      domain_findings: draft.findings,
      outcome: draft.outcome || null,
      rationale: draft.rationale || null,
      status: sign ? 'signed' : 'in_progress',
      ...(sign ? { signed_at: new Date().toISOString() } : {}),
    }
    let q
    if (draft.evaluation_id) {
      q = supabase.schema('clinical').from('evaluation').update(payload).eq('evaluation_id', draft.evaluation_id)
    } else {
      // org_id via public.get_my_org_member (TABLE-returning RPC → PostgREST returns an array)
      const { data: members } = await supabase.rpc('get_my_org_member')
      const orgId = members?.[0]?.org_id
      if (!orgId) { setErr('Organization context unavailable — cannot save consultation. Sign out and back in, then retry.'); setBusy(false); return }
      q = supabase.schema('clinical').from('evaluation').insert({ ...payload, org_id: orgId })
    }
    const { error } = await q
    setBusy(false)
    if (error) { setErr(error.message); return }
    setDraft(null); await load()
  }

  if (draft) {
    const t = draft.template
    const modules = t.specialty_modules ? Object.keys(t.specialty_modules) : []
    return (
      <div style={{ padding: 24, maxWidth: 860 }}>
        <h2 style={{ color: '#e5e7eb', fontFamily: 'Rajdhani, sans-serif' }}>{t.name}</h2>
        <div style={{ color: '#8a9bc0', fontSize: 13, marginBottom: 16 }}>{t.description}</div>
        {err && <div style={{ color: '#ff8a8a', marginBottom: 12 }}>{err}</div>}
        <div style={card}>
          <label style={{ fontSize: 12, color: '#c9a96e' }}>Patient ID</label>
          <input style={{ ...inp, width: 180, marginBottom: 12 }} value={draft.patient_id}
            onChange={e => setDraft({ ...draft, patient_id: e.target.value })} />
          {modules.length > 0 && (<>
            <label style={{ display: 'block', fontSize: 12, color: '#c9a96e', marginBottom: 4 }}>Specialty module</label>
            <select style={{ ...inp, marginBottom: 4 }} value={draft.module}
              onChange={e => setDraft({ ...draft, module: e.target.value })}>
              <option value="">Core consultation only</option>
              {modules.map(m => <option key={m} value={m}>{m.replaceAll('_', ' ')}</option>)}
            </select>
            {draft.module && (
              <ul style={{ color: '#8a9bc0', fontSize: 12, lineHeight: 1.7, paddingLeft: 18 }}>
                {(t.specialty_modules[draft.module] ?? []).map(x => <li key={x}>{x}</li>)}
              </ul>
            )}
          </>)}
        </div>
        {(t.domains ?? []).map(d => (
          <div key={d.key} style={card}>
            <h3 style={h}>{d.name}{d.required ? ' *' : ''}</h3>
            <textarea rows={3} style={inp} value={draft.findings[d.key] ?? ''}
              onChange={e => setDraft({ ...draft, findings: { ...draft.findings, [d.key]: e.target.value } })} />
          </div>
        ))}
        <div style={card}>
          <h3 style={h}>Signed Conclusion (one of four — no pass/fail)</h3>
          {(t.outcomes ?? []).map(o => (
            <label key={o} style={{ display: 'flex', gap: 8, color: '#e5e7eb', fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
              <input type="radio" name="outcome" checked={draft.outcome === o}
                onChange={() => setDraft({ ...draft, outcome: o })} /> {o}
            </label>
          ))}
          <label style={{ display: 'block', fontSize: 12, color: '#c9a96e', margin: '10px 0 4px' }}>Narrative rationale *</label>
          <textarea rows={4} style={inp} value={draft.rationale}
            onChange={e => setDraft({ ...draft, rationale: e.target.value })} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={ghost} disabled={busy} onClick={() => save(false)}>Save In Progress</button>
          <button style={btn} disabled={busy} onClick={() => save(true)}>{busy ? 'Saving…' : 'Sign Evaluation'}</button>
          <button style={ghost} onClick={() => setDraft(null)}>Cancel</button>
        </div>
        <div style={{ color: '#8a9bc0', fontSize: 12, marginTop: 12 }}>
          Signed evaluations are immutable. The surgeon/program retains the procedural decision.
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>
      <h2 style={{ color: '#e5e7eb', fontFamily: 'Rajdhani, sans-serif' }}>Behavioral Health Consultations</h2>
      {err && <div style={{ color: '#ff8a8a' }}>{err}</div>}
      {templates.map(t => (
        <div key={t.template_key} style={card}>
          <h3 style={h}>{t.name}</h3>
          <div style={{ color: '#8a9bc0', fontSize: 13, marginBottom: 10 }}>{t.description}</div>
          <button style={btn} onClick={() => start(t)}>Begin Evaluation</button>
        </div>
      ))}
      <div style={card}>
        <h3 style={h}>Recent Evaluations</h3>
        {evals.length === 0 && <div style={{ color: '#8a9bc0', fontSize: 13 }}>None yet.</div>}
        {evals.map(e => (
          <div key={e.evaluation_id} style={{ color: '#e5e7eb', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #1f2b45' }}>
            #{e.evaluation_id} · patient {e.patient_id} · {e.template_key}{e.specialty_module ? ` · ${e.specialty_module}` : ''} ·
            {' '}{e.status}{e.outcome ? ` — ${e.outcome}` : ''}
          </div>
        ))}
      </div>
    </div>
  )
}
